import { BikeType, RidingStyle, RidingExperience, Flexibility } from './bikeConfig';

export type AngleStatus = 'good' | 'borderline' | 'needs_attention' | 'no_range';

export type AngleKey = 'knee_bdc' | 'hip_tdc' | 'trunk' | 'elbow';

interface AngleRange {
  min: number;
  max: number;
}

type StyleRanges = Partial<Record<RidingStyle, Record<AngleKey, AngleRange | null>>>;

export const BORDERLINE_BUFFER = 5; // degrees

export const HARD_LIMITS: Partial<Record<AngleKey, { absolute_min?: number; absolute_max?: number }>> = {
  knee_bdc: { absolute_min: 125, absolute_max: 155 },
  hip_tdc:  { absolute_min: 35 },
  trunk:    { absolute_min: 15, absolute_max: 85 },
  elbow:    { absolute_min: 70, absolute_max: 180 },
};

interface ShiftModifier {
  min_shift: number;
  max_shift: number;
}

type ExperienceModifiers = Record<RidingExperience, Record<AngleKey, ShiftModifier>>;

export const EXPERIENCE_MODIFIERS: ExperienceModifiers = {
  beginner: {
    knee_bdc: { min_shift: -2, max_shift: -2 },
    hip_tdc:  { min_shift:  5, max_shift:  5 },
    trunk:    { min_shift:  3, max_shift:  3 },
    elbow:    { min_shift:  0, max_shift:  0 },
  },
  intermediate: {
    knee_bdc: { min_shift: 0, max_shift: 0 },
    hip_tdc:  { min_shift: 0, max_shift: 0 },
    trunk:    { min_shift: 0, max_shift: 0 },
    elbow:    { min_shift: 0, max_shift: 0 },
  },
  experienced: {
    knee_bdc: { min_shift:  2, max_shift:  2 },
    hip_tdc:  { min_shift: -3, max_shift: -3 },
    trunk:    { min_shift: -2, max_shift: -2 },
    elbow:    { min_shift:  0, max_shift:  0 },
  },
};

interface FlexibilityModifier {
  min_shift: number;
}

type FlexibilityModifiers = Record<Flexibility, Record<AngleKey, FlexibilityModifier>>;

export const FLEXIBILITY_MODIFIERS: FlexibilityModifiers = {
  good: {
    knee_bdc: { min_shift: 0 },
    hip_tdc:  { min_shift: 0 },
    trunk:    { min_shift: 0 },
    elbow:    { min_shift: 0 },
  },
  average: {
    knee_bdc: { min_shift: 0 },
    hip_tdc:  { min_shift: 5 },
    trunk:    { min_shift: 3 },
    elbow:    { min_shift: 0 },
  },
  limited: {
    knee_bdc: { min_shift:  0 },
    hip_tdc:  { min_shift: 10 },
    trunk:    { min_shift:  7 },
    elbow:    { min_shift:  0 },
  },
};

// Reference ranges for every valid bikeType × ridingStyle combination.
// null means the combination is not valid / not applicable.
export const ANGLE_REFERENCE_RANGES: Record<BikeType, StyleRanges> = {
  road: {
    comfort: {
      knee_bdc: { min: 137, max: 147 },
      hip_tdc:  { min: 55,  max: 75  },
      trunk:    { min: 42,  max: 52  },
      elbow:    { min: 150, max: 170 },
    },
    balanced: {
      knee_bdc: { min: 139, max: 149 },
      hip_tdc:  { min: 45,  max: 65  },
      trunk:    { min: 35,  max: 45  },
      elbow:    { min: 150, max: 170 },
    },
    performance: {
      knee_bdc: { min: 141, max: 151 },
      hip_tdc:  { min: 38,  max: 58  },
      trunk:    { min: 28,  max: 38  },
      elbow:    { min: 150, max: 170 },
    },
  },
  gravel: {
    comfort: {
      knee_bdc: { min: 136, max: 146 },
      hip_tdc:  { min: 58,  max: 78  },
      trunk:    { min: 45,  max: 55  },
      elbow:    { min: 150, max: 170 },
    },
    balanced: {
      knee_bdc: { min: 138, max: 148 },
      hip_tdc:  { min: 48,  max: 68  },
      trunk:    { min: 38,  max: 48  },
      elbow:    { min: 150, max: 170 },
    },
    performance: {
      knee_bdc: { min: 140, max: 150 },
      hip_tdc:  { min: 42,  max: 62  },
      trunk:    { min: 32,  max: 42  },
      elbow:    { min: 150, max: 170 },
    },
  },
  trekking: {
    comfort: {
      knee_bdc: { min: 135, max: 145 },
      hip_tdc:  { min: 65,  max: 85  },
      trunk:    { min: 50,  max: 65  },
      elbow:    { min: 150, max: 170 },
    },
    balanced: {
      knee_bdc: { min: 137, max: 147 },
      hip_tdc:  { min: 55,  max: 75  },
      trunk:    { min: 45,  max: 55  },
      elbow:    { min: 150, max: 170 },
    },
    performance: {
      knee_bdc: { min: 139, max: 149 },
      hip_tdc:  { min: 50,  max: 70  },
      trunk:    { min: 40,  max: 50  },
      elbow:    { min: 150, max: 170 },
    },
  },
  mountain: {
    comfort: {
      knee_bdc: { min: 133, max: 143 },
      hip_tdc:  { min: 60,  max: 80  },
      trunk:    { min: 45,  max: 60  },
      elbow:    { min: 150, max: 170 },
    },
    balanced: {
      knee_bdc: { min: 135, max: 145 },
      hip_tdc:  { min: 50,  max: 70  },
      trunk:    { min: 40,  max: 50  },
      elbow:    { min: 150, max: 170 },
    },
    performance: {
      knee_bdc: { min: 137, max: 147 },
      hip_tdc:  { min: 45,  max: 65  },
      trunk:    { min: 35,  max: 45  },
      elbow:    { min: 150, max: 170 },
    },
  },
  city: {
    comfort: {
      knee_bdc: { min: 133, max: 143 },
      hip_tdc:  { min: 75,  max: 100 },
      trunk:    { min: 60,  max: 80  },
      elbow:    { min: 155, max: 175 },
    },
    // balanced and performance are not valid for city bikes
    balanced:    null,
    performance: null,
  },
  triathlon: {
    // comfort is not valid for triathlon bikes
    comfort: null,
    balanced: {
      knee_bdc: { min: 141, max: 151 },
      hip_tdc:  { min: 40,  max: 60  },
      trunk:    { min: 25,  max: 35  },
      elbow:    { min: 85,  max: 110 },
    },
    performance: {
      knee_bdc: { min: 143, max: 153 },
      hip_tdc:  { min: 35,  max: 55  },
      trunk:    { min: 18,  max: 28  },
      elbow:    { min: 85,  max: 110 },
    },
  },
};
